import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { ButtonToolbar, Button, Glyphicon } from "react-bootstrap";
import toastr from "toastr";
import Swal from "sweetalert2";

import {
  ROLENAMES,
  LICENSE_KEY,
  DEFINE_LICENSE_KEY,
} from "provider/PermissionProvider/permissions";
import usePermission from "provider/PermissionProvider";

import {
  getLicenses,
  addLicenseAndMap,
  updateLicenseAndMapping,
  deleteLicense,
} from "./action";
import { LicensesActivityLogsPath } from "routes/paths";
import Loader from "../../Loader/index";
import { Filters } from "../../Filter";
import { famOption } from "../../Filter/Filters";
import { LicenseFormModal } from "./Form/LicenseFormModal";
import { Panel, PanelHeader } from "../../Panel";
import { RemoteGrid } from "../../Grid";
import {
  BoundTo,
  ProductType,
  UsageLimitType,
  FormMode,
  LicensesListColumns,
  LicensesFilters,
  Order,
  getKeyByValue,
} from "../../../constant";

const initialLicense = {
  productId: "",
  productNumber: "",
  packageId: "",
  packageName: "",
  duration: "",
  numberOfUnlock: "",
  requiresOnline: Number(false),
  featureIds: [""],
  requiredFirmwareVersions: [""],
  units: [
    {
      family: "-1",
      type: "-1",
      versionStart: "",
      versionEnd: "",
    },
  ],

  boundTo: BoundTo.UnitFamilyTypeVersion.displayName,
  productType: ProductType.Digital.displayName,
  usageLimitType: UsageLimitType.Unlimited.displayName,
  usageLimit: 0,
  signatureSize: 32,
  requiresLogin: false,
};

const Licenses = (props) => {
  const { permissionContext } = usePermission();
  const { hasPermission } = permissionContext.permissions;
  const [isLoading, setIsLoading] = useState(true);
  const [isLicenseFormModalOpen, setIsLicenseFormModalOpen] = useState(false);
  const [license, setLicense] = useState(initialLicense);
  const [licenseFormMode, setLicenseFormMode] = useState(FormMode.Add);
  const [selectedFamily, setSelectedFamily] = useState(-1);
  const [selectedType, setSelectedType] = useState(-1);
  const [isGridLoading, setIsGridLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    order: { sortBy: LicensesListColumns.product_number, orderBy: Order.asc },
    search: "",

    searchBy: 0,
    operation: 0,
  });

  const refreshLicenses = () => {
    return props.getLicenses(
      pagination.currentPage,
      pagination.pageSize,
      pagination.order.sortBy,
      pagination.order.orderBy,
      pagination.search,
      selectedFamily,
      selectedType,
      pagination.searchBy,
      pagination.operation
    );
  };

  useEffect(() => {
    refreshLicenses().then(() => {
      setIsLoading(false);
      setIsGridLoading(false);
    });
  }, []);

  const handleLicenseFormModalOpen = () => {
    setIsLicenseFormModalOpen(true);
  };

  const handleLicenseFormModalClose = () => {
    setIsLicenseFormModalOpen(false);
  };

  const handleAddLicenseClick = () => {
    setLicenseFormMode(FormMode.Add);
    setLicense({
      ...initialLicense,
      units: [
        {
          ...license.units[0],
          family: selectedFamily,
          type: selectedType,
          versionStart: "",
          versionEnd: "",
        },
      ],
    });

    handleLicenseFormModalOpen();
  };

  const handleViewLicenseDetailsClick = (row) => (event) => {
    const units = [];

    row.units.map((unit) =>
      units.push({
        family: unit.family_id,
        type: unit.type_id,
        versionStart: unit.version_start,
        versionEnd: unit.version_end,
      })
    );

    setLicenseFormMode(FormMode.View);
    setLicense({
      productId: row.product_id,
      productNumber: row.product_number,
      packageId: row.package_id,
      packageName: row.package_name,
      duration: row.duration,
      numberOfUnlock: row.number_of_unlock,
      requiresOnline: row.requires_online,
      featureIds: row.feature_ids,
      requiredFirmwareVersions: row.requires_fw_vers,

      boundTo: BoundTo[getKeyByValue(BoundTo, row.bound_to)].displayName,
      productType:
        ProductType[getKeyByValue(ProductType, row.product_type)].displayName,
      usageLimitType: row.usage_limit_type,
      usageLimit: row.usage_limit,
      signatureSize: row.signature_size,
      requiresLogin: row.requires_login,
      units: units,
    });

    handleLicenseFormModalOpen();
  };

  const handleAddLicense = async (license) => {
    const response = await props.addLicenseAndMap(license);

    if (response.isSuccess) {
      handleLicenseFormModalClose();
      toastr.info(`New license has been added.`, "Add");

      await refreshLicenses();
    } else {
      toastr.error(response.payload.error, "Error");
    }
  };

  const handleUpdateLicense = async (productId, license) => {
    const response = await props.updateLicenseAndMapping(productId, license);
    if (response.isSuccess) {
      handleLicenseFormModalClose();
      toastr.info("Product successfully updated.", "Update");

      await refreshLicenses();
    } else {
      toastr.error(response.payload.error, "Error");
    }
  };

  const handleDeleteLicense = (license) => (event) => {
    Swal.fire({
      title: "Delete license?",
      text: `Do you really want to delete license for '${license.product_number}'? This process cannot be undone.`,
      type: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No",
      reverseButtons: true,
      focusConfirm: false,
      focusCancel: true,
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: async () => {
        try {
          return await props.deleteLicense(license.product_id);
        } catch (error) {
          Swal.fire(
            "An error has occurred",
            "An unexpected error has occurred. Please try again later.",
            "error"
          );
        }
      },
    }).then(async (response) => {
      if (response.value) {
        if (response.value.isSuccess) {
          Swal.fire("Deleted!", response.value.payload.message, "success");
          await refreshLicenses();
        } else {
          Swal.fire(
            "An error has occurred",
            response.value.payload.error,
            "error"
          );
        }
      }
    });
  };

  const handleLicenseFormSubmit = async (values, actions) => {
    if (values.productId) {
      await handleUpdateLicense(values.productId, values);
    } else {
      await handleAddLicense(values);
    }

    actions.setSubmitting(false);
  };

  const handleTableChange = async (type, state) => {
    setPagination({
      ...pagination,
      pageSize: state.sizePerPage,
      currentPage: state.page,
      order: {
        sortBy: LicensesListColumns[state.sortField],
        orderBy: Order[state.sortOrder],
      },
    });
  };

  const searchGrid = async () => {
    setIsGridLoading(true);

    await props.getLicenses(
      pagination.currentPage,
      pagination.pageSize,
      pagination.order.sortBy,
      pagination.order.orderBy,
      pagination.search,
      selectedFamily,
      selectedType,
      pagination.searchBy,
      pagination.operation
    );

    setIsGridLoading(false);
  };

  const handleSearch = async (searchCriteria) => {
    setPagination({
      ...pagination,
      currentPage: 1,
      operation: searchCriteria.operation,
      search: searchCriteria.searchText,
      searchBy: searchCriteria.searchBy,
    });
  };

  useEffect(() => {
    searchGrid();
  }, [pagination]);

  const handleFilterChange = async (state) => {
    setSelectedFamily(state.family.value);
    setSelectedType(state.type.value);

    setPagination({
      ...pagination,
      currentPage: 1,
    });
  };

  const enableAddLicense = () => {
    return (
      selectedFamily !== -1 &&
      hasPermission(selectedFamily, LICENSE_KEY, DEFINE_LICENSE_KEY)
    );
  };

  const columns = [
    {
      dataField: "id",
      text: "Id",
      hidden: true,
      style: { wordWrap: "break-word" },
      headerStyle: { wordWrap: "break-word" },
      title: (cell) => cell,
    },
    {
      dataField: "product_number",
      text: "Product Number",
      sort: true,
      style: { wordWrap: "break-word" },
      headerStyle: { wordWrap: "break-word" },
      title: (cell) => cell,
    },
    {
      dataField: "units[0].family_name",
      text: "Family",
      style: { wordWrap: "break-word" },
      headerStyle: { wordWrap: "break-word" },
      title: (cell) => cell,
    },
    {
      dataField: "units[0].type_name",
      text: "Type",
      style: { wordWrap: "break-word" },
      headerStyle: { wordWrap: "break-word" },
      title: (cell) => cell,
    },
    {
      dataField: "package_id",
      text: "Package Id",
      sort: true,
      style: { wordWrap: "break-word" },
      headerStyle: { wordWrap: "break-word" },
      title: (cell) => cell,
    },
    {
      dataField: "package_name",
      text: "Package Name",
      sort: true,
      style: { wordWrap: "break-word" },
      headerStyle: { wordWrap: "break-word" },
      title: (cell) => cell,
    },
    {
      dataField: "buttons",
      text: "",
      formatter: (cell, row, rowIndex) => (
        <div className="btn-toolbar">
          <button
            className="btn btn-primary"
            title="View Details"
            onClick={handleViewLicenseDetailsClick(row)}
          >
            <span className="glyphicon glyphicon-eye-open" aria-hidden="true" />
          </button>
          <button
            disabled={
              !hasPermission(
                row.units[0].family_id,
                LICENSE_KEY,
                DEFINE_LICENSE_KEY
              )
            }
            className="btn btn-danger"
            title="Delete License"
            onClick={handleDeleteLicense(row)}
          >
            <span className="glyphicon glyphicon-trash" aria-hidden="true" />
          </button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <Loader fullpage message="Loading licenses" />;
  }

  return (
    <div className="container-fluid">
      <h2>Licenses</h2>
      <div className="row">
        <div className="col-md-10 col-md-push-2">
          <Panel>
            <PanelHeader>
              <ButtonToolbar>
                {
                  <button
                    className="btn btn-sm btn-success"
                    disabled={!enableAddLicense()}
                    onClick={handleAddLicenseClick}
                  >
                    <span
                      className="glyphicon glyphicon-plus"
                      aria-hidden="true"
                    />{" "}
                    Add License
                  </button>
                }

                <Button
                  bsSize="small"
                  bsStyle="primary"
                  onClick={() => props.history.push(LicensesActivityLogsPath)}
                >
                  <Glyphicon glyph="tasks" /> View Activity Logs
                </Button>
              </ButtonToolbar>
            </PanelHeader>
            <div className="panel-body">
              <RemoteGrid
                filterFields={LicensesFilters}
                data={props.licenses}
                columns={columns}
                loading={isGridLoading}
                pagination={pagination}
                onSearch={handleSearch}
                onTableChange={handleTableChange}
              />
            </div>
          </Panel>
        </div>
        <Filters
          className="col-md-2 col-md-pull-10"
          onFilterValueChange={handleFilterChange}
          familyOption={famOption.FAMILY_UNITS}
          roleNameId={ROLENAMES[LICENSE_KEY].id}
          hasFamily
          hasType
        />
      </div>
      <LicenseFormModal
        key={license.productId}
        license={license}
        show={isLicenseFormModalOpen}
        onFormSubmit={handleLicenseFormSubmit}
        onModalClose={handleLicenseFormModalClose}
        mode={licenseFormMode}
        canDefineLicense={hasPermission(
          license.units[0].family,
          LICENSE_KEY,
          DEFINE_LICENSE_KEY
        )}
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    licenses: state.entitlement.licenses,
    currentUser: state.account.currentUser,
  };
};

const mapDispatchToProps = {
  getLicenses,
  addLicenseAndMap,
  updateLicenseAndMapping,
  deleteLicense,
};

export default connect(mapStateToProps, mapDispatchToProps)(Licenses);
